import {useMemo, useState} from 'react';
import {Check, ChevronRight, Plus, Search, X} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/matic/components/ui/dialog';
import {Button} from '@/matic/components/ui/button';
import {Checkbox} from '@/matic/components/ui/checkbox';
import {Input} from '@/matic/components/ui/input';
import {useMasterDataPermissions} from '@/matic/features/admin/master-data-manager/hooks/useMasterDataPermissions';
import {useObjectTypes} from '@/matic/features/master-data/hooks/useObjectTypes';
import {useObjectRecordsByType} from '@/matic/features/master-data/hooks/useObjectRecordsByType';
import type {
  ObjectRecord,
  ObjectType,
} from '@/matic/features/master-data/types/master-data-types';
import {cn} from '@/matic/lib/utils/cn';
import {RelayCreateDialog} from './RelayCreateDialog';
import {
  CATEGORY_SCOPE_RECORD_ID,
  isCategoryScopeItem,
  type ScopeItem,
} from '@/matic/features/relay/lib/scope-utils';

/** "1 record" / "3 records and 2 categories" / "the entire Projects category". */
function describeSelection(items: ScopeItem[], types: ObjectType[]): string {
  const categories = items.filter((item) => isCategoryScopeItem(item));
  const recordCount = items.length - categories.length;
  const recordPhrase =
    recordCount === 1 ? '1 record' : `${recordCount} records`;
  if (categories.length === 0) return recordPhrase;
  if (categories.length === 1) {
    const typeName = types.find((t) => t.id === categories[0].typeId)?.name;
    const categoryPhrase = typeName
      ? `the entire ${typeName} category`
      : '1 entire category';
    return recordCount === 0
      ? categoryPhrase
      : `${recordPhrase} and ${categoryPhrase}`;
  }

  const categoryPhrase = `${categories.length} entire categories`;
  return recordCount === 0
    ? categoryPhrase
    : `${recordPhrase} and ${categoryPhrase}`;
}

type ScopePickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Whether the user already has a desktop shortcuts folder. Gates the
   * "also update" checkbox — with no folder there is nothing to update.
   */
  canUpdateDesktop?: boolean;
  /** Fires when the user names and saves the selected scope as a Page. */
  onSave: (
    name: string,
    items: ScopeItem[],
    alsoUpdateDesktop: boolean
  ) => void;
};

/**
 * The Scope Picker — a centered modal listing every object type and its
 * records. The user highlights the records they care about, clicks
 * "Add Scope to Sidebar", names the result, and it becomes a persistent
 * "Page" entry in the sidebar rail (persisted as a RelayPin).
 *
 * Deliberately not built on `AssociationPicker`/`MdmObjectPicker`
 * (`_shared/components/MdmObjectPicker`), the codebase's canonical
 * cross-domain type/record picker: that component is a compact combobox
 * that closes on each single selection (callers stack multiple opens to
 * build a chip list) and its inline-create only creates records of an
 * existing type. This picker needs a persistent multi-select surface
 * (checkable rows accumulating across many types in one open dialog) plus
 * "+ New type…" creation via `RelayCreateDialog` — neither fits
 * `AssociationPicker`'s shape without forcing it into something it isn't.
 * Per uxConventions.md §4.1, this is the documented fork exception, not
 * drift — the picker source is the same, but the interaction shape
 * genuinely diverges.
 */
export function ScopePickerDialog({
  open,
  onOpenChange,
  canUpdateDesktop = false,
  onSave,
}: ScopePickerDialogProps) {
  const {canWrite} = useMasterDataPermissions();
  const [step, setStep] = useState<'pick' | 'name'>('pick');
  const [selected, setSelected] = useState<Map<string, ScopeItem>>(new Map());
  const [filter, setFilter] = useState('');
  const [pageName, setPageName] = useState('');
  const [alsoUpdateDesktop, setAlsoUpdateDesktop] = useState(false);
  const [expandedTypeIds, setExpandedTypeIds] = useState<Set<string>>(
    new Set()
  );
  // While a create flow is open, the picker hides underneath (its selection
  // state survives — only the Dialog shell unmounts) instead of stacking two
  // modals; closing the create dialog brings the picker back.
  const [createState, setCreateState] = useState<
    | {kind: 'newType'}
    | {kind: 'record'; typeId: string; typeName: string}
    | null
  >(null);

  const typesQuery = useObjectTypes({limit: 100});
  const types = useMemo<ObjectType[]>(
    () => typesQuery.data?.items ?? [],
    [typesQuery.data]
  );
  const typeIds = useMemo(() => types.map((t) => t.id), [types]);
  const {recordsByType, isLoading: recordsLoading} =
    useObjectRecordsByType(typeIds);

  const isLoading = typesQuery.isLoading || recordsLoading;

  const normalizedFilter = filter.trim().toLowerCase();
  const filteredSections = useMemo(
    () =>
      types
        .map((type) => {
          const records = recordsByType[type.id] ?? [];
          if (!normalizedFilter) return {type, records, matchedByName: false};
          if (type.name.toLowerCase().includes(normalizedFilter)) {
            return {type, records, matchedByName: true};
          }

          return {
            type,
            records: records.filter((record) =>
              (record.display ?? record.code)
                .toLowerCase()
                .includes(normalizedFilter)
            ),
            matchedByName: false,
          };
        })
        .filter(
          (section) =>
            !normalizedFilter ||
            section.matchedByName ||
            section.records.length > 0
        ),
    [types, recordsByType, normalizedFilter]
  );

  const selectedItems = useMemo(() => [...selected.values()], [selected]);

  const resetAndClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStep('pick');
      setSelected(new Map());
      setFilter('');
      setPageName('');
      setAlsoUpdateDesktop(false);
      setExpandedTypeIds(new Set());
    }

    onOpenChange(nextOpen);
  };

  const toggleTypeExpanded = (typeId: string) => {
    setExpandedTypeIds((previous) => {
      const next = new Set(previous);
      if (next.has(typeId)) {
        next.delete(typeId);
      } else {
        next.add(typeId);
      }

      return next;
    });
  };

  const toggleRecord = (typeId: string, record: ObjectRecord) => {
    setSelected((previous) => {
      const key = `${typeId}:${record.id}`;
      const next = new Map(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // A record selection supersedes the type-wide "All" selection — the
        // category already covers every record, so keeping both would save a
        // redundant scope (`type:*` + `type:record`).
        next.delete(`${typeId}:${CATEGORY_SCOPE_RECORD_ID}`);
        next.set(key, {typeId, recordId: record.id});
      }

      return next;
    });
  };

  const toggleCategory = (typeId: string) => {
    setSelected((previous) => {
      const key = `${typeId}:${CATEGORY_SCOPE_RECORD_ID}`;
      const next = new Map(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // "All {type}" covers every record — drop that type's individual
        // record selections rather than saving them redundantly.
        for (const existingKey of next.keys()) {
          if (existingKey.startsWith(`${typeId}:`)) next.delete(existingKey);
        }

        next.set(key, {typeId, recordId: CATEGORY_SCOPE_RECORD_ID});
      }

      return next;
    });
  };

  const goToNameStep = () => {
    // Suggest a name from the selection so single-selection pages are one
    // click — the record's display name, or the type's name for an "All
    // {type}" category selection.
    if (!pageName) {
      const first = selectedItems[0];
      let suggestion = '';
      if (first && isCategoryScopeItem(first)) {
        suggestion = types.find((t) => t.id === first.typeId)?.name ?? '';
      } else if (first) {
        const record = (recordsByType[first.typeId] ?? []).find(
          (r) => r.id === first.recordId
        );
        suggestion = record ? (record.display ?? record.code) : '';
      }

      setPageName(
        selectedItems.length === 1
          ? suggestion
          : suggestion && selectedItems.length > 1
            ? `${suggestion} +${selectedItems.length - 1}`
            : ''
      );
    }

    setStep('name');
  };

  const handleSave = () => {
    const name = pageName.trim();
    if (!name || selectedItems.length === 0) return;
    onSave(name, selectedItems, alsoUpdateDesktop);
    resetAndClose(false);
  };

  return (
    <>
      <Dialog open={open && !createState} onOpenChange={resetAndClose}>
        <DialogContent
          size="lg"
          className={step === 'pick' ? 'h-[70vh]' : undefined}
        >
          {step === 'pick' ? (
            <>
              <DialogHeader className="mb-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle>Scope Picker</DialogTitle>
                    <DialogDescription>
                      Highlight the records — or entire categories — this Page
                      should cover.
                    </DialogDescription>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => {
                      resetAndClose(false);
                    }}
                    className="rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <Input
                    value={filter}
                    placeholder="Search types and records..."
                    className="pl-9"
                    onChange={(event) => {
                      setFilter(event.target.value);
                    }}
                  />
                </div>
              </DialogHeader>

              <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-neutral-200">
                {isLoading ? (
                  <div className="p-4 text-sm text-neutral-500">
                    Loading records...
                  </div>
                ) : filteredSections.length === 0 ? (
                  <div className="p-4 text-sm text-neutral-500">
                    {normalizedFilter
                      ? 'No types or records match this search.'
                      : 'No records yet.'}
                  </div>
                ) : (
                  filteredSections.map(({type, records}) => {
                    // Sections default collapsed so every type is visible at a
                    // glance; searching force-expands matches so filter results
                    // are never hidden behind a collapsed header.
                    const isExpanded =
                      normalizedFilter !== '' || expandedTypeIds.has(type.id);
                    const categorySelected = selected.has(
                      `${type.id}:${CATEGORY_SCOPE_RECORD_ID}`
                    );
                    const selectedInType = records.filter((record) =>
                      selected.has(`${type.id}:${record.id}`)
                    ).length;
                    return (
                      <div
                        key={type.id}
                        className="border-b border-neutral-100 last:border-b-0"
                      >
                        <button
                          type="button"
                          aria-expanded={isExpanded}
                          onClick={() => {
                            toggleTypeExpanded(type.id);
                          }}
                          className="sticky top-0 flex w-full items-center gap-1.5 bg-neutral-50 px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                        >
                          <ChevronRight
                            className={cn(
                              'h-3 w-3 shrink-0 transition-transform duration-150',
                              isExpanded && 'rotate-90'
                            )}
                          />
                          <span className="flex-1 truncate">{type.name}</span>
                          {(categorySelected || selectedInType > 0) && (
                            <span className="rounded-full bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-primary-600">
                              {categorySelected
                                ? 'All selected'
                                : `${selectedInType} selected`}
                            </span>
                          )}
                          <span className="text-[10px] font-medium tracking-normal text-neutral-400">
                            {records.length}
                          </span>
                        </button>
                        {isExpanded && (
                          <>
                            {/* Type-wide selection — mirrors AssociationPicker's
                                "Just {Category}" opt-in row; saves the canonical
                                wildcard scope (`typeId:*`, see scope-utils). */}
                            <button
                              type="button"
                              onClick={() => {
                                toggleCategory(type.id);
                              }}
                              className={cn(
                                'flex w-full items-center gap-2 border-l-2 px-3 py-1.5 pl-7 text-left text-sm italic transition-colors',
                                categorySelected
                                  ? 'border-l-primary-500 bg-primary-50 font-medium text-neutral-900'
                                  : 'border-l-transparent text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700'
                              )}
                            >
                              <span className="flex-1 truncate">
                                All {type.name}
                              </span>
                              {categorySelected && (
                                <Check className="h-4 w-4 shrink-0 text-primary-500" />
                              )}
                            </button>
                            {records.map((record) => {
                              const isSelected = selected.has(
                                `${type.id}:${record.id}`
                              );
                              return (
                                <button
                                  key={record.id}
                                  type="button"
                                  onClick={() => {
                                    toggleRecord(type.id, record);
                                  }}
                                  className={cn(
                                    'flex w-full items-center gap-2 border-l-2 px-3 py-1.5 pl-7 text-left text-sm transition-colors',
                                    isSelected
                                      ? 'border-l-primary-500 bg-primary-50 font-medium text-neutral-900'
                                      : 'border-l-transparent text-neutral-700 hover:bg-neutral-50'
                                  )}
                                >
                                  <span className="flex-1 truncate">
                                    {record.display ?? record.code}
                                  </span>
                                  {isSelected && (
                                    <Check className="h-4 w-4 shrink-0 text-primary-500" />
                                  )}
                                </button>
                              );
                            })}
                            {canWrite && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCreateState({
                                    kind: 'record',
                                    typeId: type.id,
                                    typeName: type.name,
                                  });
                                }}
                                className="flex w-full items-center gap-2 border-l-2 border-l-transparent px-3 py-1.5 pl-7 text-left text-sm text-neutral-400 transition-colors hover:bg-neutral-50 hover:text-neutral-600"
                              >
                                <Plus className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">
                                  New {type.name.toLowerCase()}…
                                </span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })
                )}
                {!isLoading && canWrite && (
                  <button
                    type="button"
                    onClick={() => {
                      setCreateState({kind: 'newType'});
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-neutral-400 transition-colors hover:bg-neutral-50 hover:text-neutral-600"
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0" />
                    <span>New type…</span>
                  </button>
                )}
              </div>

              <DialogFooter className="mt-4 items-center">
                <span className="mr-auto text-sm text-neutral-500">
                  {selectedItems.length === 0
                    ? 'Nothing selected'
                    : `${selectedItems.length} selected`}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => {
                    resetAndClose(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="dark"
                  disabled={selectedItems.length === 0}
                  onClick={goToNameStep}
                >
                  Add Scope to Sidebar
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader className="mb-3">
                <DialogTitle>Name this Page</DialogTitle>
                <DialogDescription>
                  {`It will appear in the sidebar, scoped to ${describeSelection(
                    selectedItems,
                    types
                  )}.`}
                </DialogDescription>
              </DialogHeader>

              <Input
                autoFocus
                value={pageName}
                placeholder="Page name"
                onChange={(event) => {
                  setPageName(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSave();
                }}
              />

              {/* Only when a desktop folder already exists — otherwise there's
                  nothing to update, and the step stays a single field. */}
              {canUpdateDesktop && (
                <div className="mt-3">
                  <Checkbox
                    checked={alsoUpdateDesktop}
                    label="Also update my desktop folder"
                    onChange={(event) => {
                      setAlsoUpdateDesktop(event.target.checked);
                    }}
                  />
                </div>
              )}

              <DialogFooter className="mt-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setStep('pick');
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="dark"
                  disabled={!pageName.trim()}
                  onClick={handleSave}
                >
                  Save Page
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {createState && (
        <RelayCreateDialog
          open
          types={types}
          contextAssociations={[]}
          initialStep={createState}
          onQuickDrop={() => {
            // Unreachable: with initialStep set the quick-drop picker step
            // never renders.
          }}
          onClose={() => {
            setCreateState(null);
          }}
        />
      )}
    </>
  );
}
